"use client";

import { createElement } from "react";
import { notificationStyle, TONE_CLASSES } from "@/lib/notifications/notification-style";
import { cn } from "@/lib/utils";
import type { Notification } from "@/types/fleet";

interface NotificationIconProps {
  /** A vehicle event type or `cost_<severity>`; unknown or missing falls back to `severity`. */
  kind?: string;
  severity: Notification["type"];
  size?: "sm" | "md";
  className?: string;
}

const SIZE_CLASSES = {
  sm: { chip: "h-6 w-6 rounded-md", icon: "h-3.5 w-3.5" },
  md: { chip: "h-9 w-9 rounded-lg", icon: "h-5 w-5" },
} as const;

/**
 * The tinted chip for a notification. Shared by the toast, the notifications list and the
 * preferences switches so all three speak the same icon language. Decorative: the title beside it
 * always names the event.
 */
export function NotificationIcon({ kind, severity, size = "md", className }: NotificationIconProps) {
  const style = notificationStyle(kind, severity);
  const sizes = SIZE_CLASSES[size];

  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        sizes.chip,
        TONE_CLASSES[style.tone][style.emphasis],
        className,
      )}
    >
      {/* `createElement` for the same react-hooks/static-components reason as `RouteEventIcon`. */}
      {createElement(style.icon, { className: sizes.icon, strokeWidth: 2 })}
    </span>
  );
}
